import { forwardRef, useImperativeHandle, useState } from "react";
import { FormGroup } from "../../../shared/form/FormGroup";
import { IReprocessMonthlyStatus, ReprocessReason } from "../../model";
import useModelReprocess from "../../hooks/useReprocess";
import Dialog from "../../../shared/components/Dialog/Dialog";
import SelectDropdown from "../../../shared/components/Dropdown/SelectDropdown";
import InputGroup, { RequiredStar } from "../../../shared/form/InputGroup";
import Label from "../../../shared/typography/Label";
import useToast from "../../../shared/hook/useToast";
import { ModelStatus } from "../../../../models/Model";
import ModelService from "../../services/ModelService";
import ProgressLoader from "../../../shared/components/ProgressLoader";
import CircleLoader from "../../../shared/components/CircleLoader";

export interface ReprocessDialogRef {
  openDialog: (model: { id: string; status: ModelStatus }) => void;
  closeDialog: () => void;
}

const reasonLabelMap: Record<ReprocessReason, string> = {
  [ReprocessReason.BAD_QUALITY]: "Bad Quality",
  [ReprocessReason.BAD_ORIENTATION]: "Bad Orientation",
  [ReprocessReason.OTHER]: "Other",
  [ReprocessReason.MODEL_FAILED]: "Model Failed",
};

const ModelReprocessDialog = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState<{
    id: string;
    status: ModelStatus;
  } | null>(null);
  const [reprocessStatus, setReprocessStatus] =
    useState<IReprocessMonthlyStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [reason, setReason] = useState<ReprocessReason | null>(null);
  const [comment, setComment] = useState<string | null>(null);

  const useReprocess = useModelReprocess();
  const toast = useToast();

  const getReprocessStatus = async (modelId: string) => {
    setIsLoading(true);
    const response = await ModelService.reprocessStatus(modelId);
    setReprocessStatus(response.data);
    setIsLoading(false);
    return response.data;
  };

  const openDialog = async (model: { id: string; status: ModelStatus }) => {
    setModel(model);
    setOpen(true);
    await getReprocessStatus(model.id);
  };

  const closeDialog = () => {
    setModel(null);
    setReason(null);
    setComment(null);
    setOpen(false);
    setReprocessStatus(null);
  };

  useImperativeHandle(ref, () => ({
    openDialog,
    closeDialog,
  }));

  const handleReprocess = async () => {
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    await useReprocess.reprocess(model, reason, comment);
    closeDialog();
  };

  const canReprocess =
    reprocessStatus && reprocessStatus.usage < reprocessStatus.limit;

  return (
    <Dialog
      title="Reprocess Model"
      open={open}
      onClose={closeDialog}
      confirmButtonProps={{
        disabled: !canReprocess,
        loading: useReprocess.isReprocessing,
        children: "Reprocess",
        onClick: handleReprocess,
      }}
      cancelButtonProps={{
        children: "Cancel",
        onClick: () => closeDialog(),
      }}
    >
      {!reprocessStatus && (
        <div className="w-full flex justify-center items-center my-4">
          <CircleLoader color="primary" size={48} />
        </div>
      )}
      {canReprocess && (
        <>
          <FormGroup label="Reason">
            <Label>
              Reason <RequiredStar>*</RequiredStar>
            </Label>
            <SelectDropdown
              options={Object.values(ReprocessReason).map((reason) => ({
                title: reasonLabelMap[reason],
                value: reason,
              }))}
              currentSelectedLabel={
                reason ? reasonLabelMap[reason] : "Select Reason"
              }
              placeholder="Select Reason"
              onSelect={(e: any) => setReason(e as ReprocessReason)}
            />
          </FormGroup>
          <FormGroup label="Comment" required>
            <Label>Comment</Label>
            <InputGroup
              bordered
              inputProps={{
                value: comment,
                placeholder: "Enter comment",
                onChange: (e: any) => setComment(e.target.value),
              }}
            />
          </FormGroup>
        </>
      )}
      {reprocessStatus && !canReprocess && (
        <div className="w-full flex justfiy-center">
          <Label>
            You used <b>{reprocessStatus.usage}</b> of{" "}
            <b>{reprocessStatus.limit}</b> reprocessing slots this month.
          </Label>
        </div>
      )}
    </Dialog>
  );
});

export default ModelReprocessDialog;
